import React, { useRef, useEffect, useCallback } from 'react';
import { McDialog, McButton } from '@maersk-global/mds-react-wrapper';

interface ConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    content: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'primary' | 'danger';
    loading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    content,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'primary',
    loading = false
}) => {
    const confirmBtnRef = useRef<any>(null);
    const lastClickTimeRef = useRef<number>(0);

    const handleConfirm = useCallback(() => {
        const now = Date.now();
        // Prevent double firing within 500ms
        if (now - lastClickTimeRef.current < 500) {
            return;
        }
        lastClickTimeRef.current = now;

        if (loading) return;
        onConfirm();
    }, [loading, onConfirm]);

    useEffect(() => {
        const btn = confirmBtnRef.current;
        if (!btn) return;

        const handler = (e: Event) => {
            e.stopPropagation();
            handleConfirm();
        };

        btn.addEventListener('click', handler);
        return () => {
            btn.removeEventListener('click', handler);
        };
    }, [handleConfirm]);

    const handleCancel = () => {
        if (loading) return;
        onClose();
    };

    return (
        <McDialog
            open={open}
            heading={title}
            closed={handleCancel}
            width="600px"
        >
            <div className="mds-text--body-regular">
                {content}
            </div>

            <McButton
                slot="secondaryAction"
                appearance="primary"
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
            >
                {cancelLabel}
            </McButton>
            <McButton
                ref={confirmBtnRef}
                slot="primaryAction"
                appearance={variant === 'danger' ? 'error' : 'primary'}
                loading={loading}
            >
                {confirmLabel}
            </McButton>
        </McDialog>
    );
};
